import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Tooltip,
  IconButton,
  Chip,
} from '@mui/material';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { getNumberComma } from '../../../utils/numberUtils';
import { useSnackbar } from 'notistack';
import InfoIcon from '@mui/icons-material/Info';
import * as taxBillApi from '../../../lib/api/taxbill';
import BillPlanEditDialog from '../../project_orders/BillPlanEditDialog';
import * as billCollectPlanApi from '../../../lib/api/billCollectPlan';

const VIEW = 'view';
const EDIT = 'edit';

// 증빙유형 상수 정의
const EVIDENCE_TYPES = {
  TAX_INVOICE: '세금계산서',
  ZERO_TAX_INVOICE: '영세율세금계산서',
  INVOICE: '계산서',
  CARD: '카드',
  CERTIFICATION: '면장',
  FOREIGN_INVOICE: '인보이스',
  FOREIGN_PURCHASE: '외환매입증명',
  OTHER: '기타',
};

// VAT가 0원인 증빙유형
const ZERO_VAT_TYPES = [
  EVIDENCE_TYPES.ZERO_TAX_INVOICE,
  EVIDENCE_TYPES.INVOICE,
  EVIDENCE_TYPES.CARD,
  EVIDENCE_TYPES.OTHER,
];

// 외환 관련 추가 필드가 필요한 증빙유형
const FOREIGN_CURRENCY_TYPES = [
  EVIDENCE_TYPES.CERTIFICATION,
  EVIDENCE_TYPES.FOREIGN_INVOICE,
  EVIDENCE_TYPES.FOREIGN_PURCHASE,
];

// 환율 통화 종류
const CURRENCY_TYPES = [
  { value: 'USD', label: 'USD (미국 달러)' },
  { value: 'EUR', label: 'EUR (유로)' },
  { value: 'JPY', label: 'JPY (일본 엔)' },
  { value: 'CNY', label: 'CNY (중국 위안)' },
  { value: 'GBP', label: 'GBP (영국 파운드)' },
];

// DatePicker 편집 셀
const DateEditInputCell = React.memo(function DateEditInputCell({ value, onChange, disabled }) {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        value={value ? dayjs(value) : null}
        onChange={onChange}
        format="YYYY-MM-DD"
        disabled={disabled}
        sx={{ width: 150 }}
        slotProps={{
          textField: {
            variant: 'standard',
            size: 'small', // 높이도 작아집니다
          },
        }}
      />
    </LocalizationProvider>
  );
});

// 숫자 입력 셀
const NumberEditInputCell = React.memo(function NumberEditInputCell({ value, onChange, disabled }) {
  return (
    <TextField
      type="number"
      value={value || 0}
      onChange={onChange}
      variant="standard"
      fullWidth
      disabled={disabled}
      InputProps={{
        inputProps: { min: 0 }
      }}
    />
  );
});

// 텍스트 입력 셀
const TextEditInputCell = React.memo(function TextEditInputCell({ value, onChange, disabled }) {
  return (
    <TextField
      type="text"
      value={value || ''}
      onChange={onChange}
      variant="standard"
      fullWidth
      disabled={disabled}
    />
  );
});

// 셀렉트 입력 셀
const SelectEditInputCell = React.memo(function SelectEditInputCell({ 
  value, 
  onChange, 
  disabled, 
  options,
  label 
}) {
  return (
    <FormControl variant="standard" fullWidth>
      <InputLabel>{label}</InputLabel>
      <Select
        value={value || ''}
        onChange={onChange}
        disabled={disabled}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
});

export default function InvoiceSplitDialog({
  open,
  onClose,
  onSave,
  prjUuid,
  partnerUuid,
  billrows,
  roUuid,
  billUuid,
  editYn,
  type,
  mode,
  partnerName,
  installmentCount,
  totalAmount,
  totalVat,
}) {
  const isView = mode === VIEW;
  const { enqueueSnackbar } = useSnackbar();

  const [rows, setRows] = useState([]);
  const [sumAmt, setSumAmt] = useState(0);
  const [sumVat, setSumVat] = useState(0);
  const [sumTot, setSumTot] = useState(0); // 총합계 상태 추가
  const [localInstallmentCount, setLocalInstallmentCount] = useState(installmentCount);
  const [loading, setLoading] = useState(true);
  // 계획(선금/중도금/잔금) 쪼개기 - 2026-10-21 수금/지급 계획 N개.
  // 2026-10-21 2차 릴리스: 이미 저장된 회차(invUuid 있음)는 서버(BillCollectPlanService)로
  // 바로 저장하고, 아직 저장 전인 새 회차(add-mode, 이 다이얼로그가 방금 만든 분할행)는 그 행의
  // planLines를 로컬 상태(rows)에만 들고 있다가 "확인"을 누를 때 onSave(rows)와 함께 통째로
  // 올라간다 - OrdersService/OrderContractService가 회차를 insert한 바로 뒤
  // BillCollectPlanService.createPlansForRound로 같은 트랜잭션 안에서 계획까지 만든다.
  const [planTarget, setPlanTarget] = useState(null);
  const [planSubmitting, setPlanSubmitting] = useState(false);

  const handleOpenPlan = async (row) => {
    if (row.invUuid) {
      try {
        const res = await billCollectPlanApi.getPlans(row.invUuid);
        setPlanTarget({
          rowId: row.id,
          invUuid: row.invUuid,
          invNo: row.invNo,
          invTot: row.invTot,
          plans: res?.data?.data || [],
        });
      } catch (err) {
        enqueueSnackbar('계획을 불러오지 못했습니다.', { variant: 'error' });
      }
      return;
    }
    // 아직 저장 전(add-mode) - 이 행에 이미 담아둔 로컬 계획 라인을 그대로 보여준다.
    // 없으면 BillPlanEditDialog가 기본값(전액 1개)으로 보여준다(서버 createPlansForRound와
    // 같은 기본값 규칙).
    setPlanTarget({
      rowId: row.id,
      invUuid: null,
      invNo: row.invTxt || `${row.id}회차`,
      invTot: row.invTot,
      plans: row.planLines || [],
    });
  };

  const handleSavePlan = async (plans) => {
    if (!planTarget) return;
    if (!planTarget.invUuid) {
      // add-mode 로컬 draft - 서버 호출 없이 이 행의 planLines만 갱신한다.
      setRows((prev) => prev.map((r) => (r.id === planTarget.rowId ? { ...r, planLines: plans } : r)));
      setPlanTarget(null);
      return;
    }
    setPlanSubmitting(true);
    try {
      await billCollectPlanApi.replacePlans(planTarget.invUuid, plans);
      enqueueSnackbar('계획을 저장했습니다.', { variant: 'success' });
      setPlanTarget(null);
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || '계획 저장에 실패했습니다.';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setPlanSubmitting(false);
    }
  };

  // 컬럼 정의 - 필드 추가
  const COLUMNS = useMemo(() => [
    { field: 'reqPlanDt', header: '발행 예정일자', type: 'date' },
    { field: 'rsltCompDt', header: '실적 기준일', type: 'date' },
    { field: 'reqCompDt', header: '발행 완료일', type: 'date', hidden: true },
    { field: 'procPlanDt', header: '수금(지급)예정일', type: 'date' },
    { field: 'invTxt', header: '발행 내역', type: 'string' },
    { field: 'evidenceType', header: '증빙', type: 'select', options: Object.entries(EVIDENCE_TYPES).map(([key, value]) => ({ value: key, label: value })) },
    { field: 'currencyType', header: '환종류', type: 'select', options: CURRENCY_TYPES, conditionalField: 'evidenceType', conditionalValues: Object.keys(EVIDENCE_TYPES).filter(key => FOREIGN_CURRENCY_TYPES.includes(EVIDENCE_TYPES[key])) },
    { field: 'foreignAmount', header: '외환 금액', type: 'number', conditionalField: 'evidenceType', conditionalValues: Object.keys(EVIDENCE_TYPES).filter(key => FOREIGN_CURRENCY_TYPES.includes(EVIDENCE_TYPES[key])) },
    { field: 'invAmt', header: '원화금액', type: 'number' },
    { field: 'invVat', header: 'VAT', type: 'number' },
    { field: 'invTot', header: '합계', type: 'number' },
  ], []);

  // 초기 상태 리셋
  const resetState = useCallback(() => {
    setLocalInstallmentCount(installmentCount);
    setRows([]);
    setSumAmt(0);
    setSumVat(0);
    setSumTot(0); // 총합계 초기화 추가
    setLoading(true);
  }, [installmentCount]);

  // 증빙유형에 따른 VAT 자동 계산 및 invTot 재계산
  const handleEvidenceTypeChange = useCallback((id, value) => {
    setRows(prev =>
      prev.map(r => {
        if (r.id === id) {
          const newRow = { ...r, evidenceType: value };
          const invAmt = Number(newRow.invAmt) || 0;
          
          // 증빙유형에 따른 VAT 계산
          if (ZERO_VAT_TYPES.includes(EVIDENCE_TYPES[value])) {
            // VAT가 0원인 증빙유형
            newRow.invVat = 0;
          } else if (value === 'TAX_INVOICE') {
            // 세금계산서인 경우 VAT 자동 계산 (10%)
            newRow.invVat = Math.round(invAmt * 0.1);
          }
          // 기타 증빙유형은 기존 VAT 값 유지
          
          // 외환 관련 증빙유형이 아닌 경우 관련 필드 초기화
          if (!FOREIGN_CURRENCY_TYPES.includes(EVIDENCE_TYPES[value])) {
            newRow.currencyType = null;
            newRow.foreignAmount = null;
          }
          
          // invTot 재계산
          const invVat = Number(newRow.invVat) || 0;
          newRow.invTot = invAmt + invVat;
          
          return newRow;
        }
        return r;
      })
    );
  }, []);

  // 셀 변경 핸들러 - 증빙유형에 따른 VAT 계산 로직 적용
  const handleCellChange = useCallback((id, field, value) => {
    if (field === 'evidenceType') {
      handleEvidenceTypeChange(id, value);
      return;
    }

    setRows(prev =>
      prev.map(r => {
        if (r.id === id) {
          const updatedRow = { ...r, [field]: value };
          
          // invAmt가 변경된 경우
          if (field === 'invAmt') {
            const invAmt = Number(value) || 0;
            
            // 증빙유형에 따른 VAT 자동 계산
            if (r.evidenceType && ZERO_VAT_TYPES.includes(EVIDENCE_TYPES[r.evidenceType])) {
              // VAT가 0원인 증빙유형
              updatedRow.invVat = 0;
            } else if (r.evidenceType === 'TAX_INVOICE') {
              // 세금계산서인 경우 VAT 자동 계산 (10%)
              updatedRow.invVat = Math.round(invAmt * 0.1);
            }
            // 기타 증빙유형은 기존 VAT 값 유지 (사용자 직접 입력)
            
            // invTot 계산
            const invVat = updatedRow.invVat !== undefined ? updatedRow.invVat : (Number(r.invVat) || 0);
            updatedRow.invTot = invAmt + invVat;
          }
          // invVat가 변경된 경우 (VAT가 0원인 증빙유형이나 세금계산서가 아닌 경우에만)
          else if (field === 'invVat') {
            const invAmt = Number(r.invAmt) || 0;
            const invVat = Number(value) || 0;
            updatedRow.invTot = invAmt + invVat;
          }
          // 다른 필드가 변경된 경우도 invTot 재계산
          else {
            const invAmt = Number(r.invAmt) || 0;
            const invVat = Number(r.invVat) || 0;
            updatedRow.invTot = invAmt + invVat;
          }
          
          return updatedRow;
        }
        return r;
      })
    );
  }, [handleEvidenceTypeChange]);

  useEffect(() => {
   // if (open) {
      resetState();
      
      if (billrows && billrows.length > 0) {
        // 기존 행에 필요한 새 필드 추가 및 증빙유형에 따른 VAT 재계산
        const updatedRows = billrows.map(row => {
          const invAmt = Number(row.invAmt) || 0;
          let invVat = Number(row.invVat) || 0;
          const evidenceType = row.evidenceType || 'TAX_INVOICE';
          
          // 증빙유형에 따른 VAT 재계산 (기존 데이터 보정)
          if (ZERO_VAT_TYPES.includes(EVIDENCE_TYPES[evidenceType])) {
            // VAT가 0원인 증빙유형
            invVat = 0;
          } else if (evidenceType === 'TAX_INVOICE') {
            // 세금계산서인 경우 VAT 재계산
            invVat = Math.round(invAmt * 0.1);
          }
          // 기타 증빙유형은 기존 VAT 값 유지
          
          return {
            ...row,
            evidenceType,
            currencyType: row.currencyType || null,
            foreignAmount: row.foreignAmount || null,
            invVat,
            invTot: row.invTot || (invAmt + invVat) // 기존 값이 있으면 사용, 없으면 계산
          };
        });
        setRows(updatedRows);
        setLoading(false);
      } else {
        // 다음 렌더링에서 초기 행을 생성하도록 빈 배열 설정
        setRows([]);
      }
   // }
  }, [open, resetState, billrows]);

  // 분할 행 계산 및 생성 함수 - invTot 계산 추가
  const calculateSplitRows = useCallback((currentRows) => {
    if (!open) return [];

    // 1) 완료된 행 분리
    const completedRows = (currentRows || [])
      .filter(r => r.reqCompYn === 'Y');

    const completedCount = completedRows.length;

    // 2) 분할 개수가 완료된 건보다 작으면 최소값 강제
    if (localInstallmentCount < completedCount) {
      setLocalInstallmentCount(completedCount);
      return currentRows;
    }

    // 3) 남은 분할 개수
    const remainCount = localInstallmentCount - completedCount;

    // 4) 완료된 금액/부가세 합계
    const sumCompletedAmt = completedRows
      .reduce((acc, r) => acc + (Number(r.invAmt) || 0), 0);
    const sumCompletedVat = completedRows
      .reduce((acc, r) => acc + (Number(r.invVat) || 0), 0);

    // 5) 전체 − 완료된 만큼 남은 금액 계산
    const remainingAmt = totalAmount - sumCompletedAmt;
    const remainingVat = totalVat - sumCompletedVat;

    // 6) 남은 금액을 remainCount로 분할
    const baseAmt = remainCount > 0 ? Math.floor(remainingAmt / remainCount) : 0;
    const remAmt = remainCount > 0 ? remainingAmt - baseAmt * remainCount : 0;
    const baseVat = remainCount > 0 ? Math.floor(remainingVat / remainCount) : 0;
    const remVat = remainCount > 0 ? remainingVat - baseVat * remainCount : 0;

    // 7) 새로 생성할 행
    const newRows = Array.from({ length: remainCount }).map((_, i) => {
      // 완료된 마지막 달 다음부터 시작
      const date = dayjs().add(i, 'month').endOf('month').format('YYYY-MM-DD');
      const invAmt = baseAmt + (i === 0 ? remAmt : 0);
      
      // 기본 증빙유형은 세금계산서, VAT는 10% 자동 계산
      const evidenceType = 'TAX_INVOICE';
      const invVat = Math.round(invAmt * 0.1); // 세금계산서는 10% VAT
      
      return {
        id: completedCount + i + 1,  // 고유 id
        billUuid,
        reqPlanDt: date,
        reqCompDt: date,
        procPlanDt: date,
        rsltCompDt: date,
        invAmt,
        invVat,
        invTot: invAmt + invVat, // 합계 자동 계산
        invTxt: partnerName,
        reqCompYn: 'N',
        evidenceType,  // 기본값 세금계산서
        currencyType: null,
        foreignAmount: null
      };
    });

    // 8) 완료된 행 + 새로 생성된 행 합치기
    return [
      ...completedRows,
      ...newRows
    ];
  }, [open, localInstallmentCount, totalAmount, totalVat, partnerName, billUuid]);

  // 초기 로딩 및 분할 개수 변경 시 행 재계산
  useEffect(() => {
    if (!open) return;
    
    // 기존 완료된 행들(reqCompYn === 'Y')은 유지
    const completedRows = rows.filter(r => r.reqCompYn === 'Y');
    
    // 새로운 행 생성 및 설정
    const newRows = calculateSplitRows(completedRows);
    
    if (newRows.length > 0) {
      setRows(newRows);
      setLoading(false);
    }
  }, [open, localInstallmentCount, calculateSplitRows]);

  // 합계 계산 - 총합계(invTot) 추가
  useEffect(() => {
    if (rows.length > 0) {
      setSumAmt(rows.reduce((acc, r) => acc + (Number(r.invAmt) || 0), 0));
      setSumVat(rows.reduce((acc, r) => acc + (Number(r.invVat) || 0), 0));
      setSumTot(rows.reduce((acc, r) => acc + (Number(r.invTot) || 0), 0)); // 총합계 추가
    } else {
      setSumAmt(0);
      setSumVat(0);
      setSumTot(0); // 총합계 초기화 추가
    }
  }, [rows]);

  // 2026-10-19 (ksm_frontend에서 포팅, 요구사항 4/11): 총액 일치 여부를 저장을 눌러야 알 수 있는 게 아니라
  // 표 위에서 바로 보이게 한다. 예전에는 저장 버튼을 눌러야 금액 불일치를 알려줬다.
  const diffAmt = Number(totalAmount) - sumAmt;
  const balanced = diffAmt === 0;

  // 분할 개수 변경 핸들러
  const handleCountChange = useCallback((e) => {
    const newCount = parseInt(e.target.value, 10);
    if (newCount > 0) setLocalInstallmentCount(newCount);
  }, []);

  // 저장 핸들러 - sumVat 기준으로 검증 변경
  const handleSave = useCallback(() => {
    // 필수 필드 검증
    const invalidRow = rows.find(row => {
      // 모든 행에 증빙유형이 있는지 확인
      if (!row.evidenceType) return true;
      
      // 외환 관련 증빙유형인 경우 필수 필드 확인
      if (FOREIGN_CURRENCY_TYPES.includes(EVIDENCE_TYPES[row.evidenceType])) {
        if (!row.currencyType || row.foreignAmount === null || row.foreignAmount === '') 
          return true;
      }
      
      return false;
    });

    if (invalidRow) {
      enqueueSnackbar('모든 필수 정보를 입력해 주세요', { variant: 'error' });
      return;
    } 

    // 금액 일치 확인
    if (sumAmt != totalAmount) {
      enqueueSnackbar('금액이 일치하지 않습니다', { variant: 'error' });
      return;
    }

    // VAT 검증 로직 개선 - 각 행의 증빙유형에 따른 VAT 계산이 올바른지 확인
    const hasVatError = rows.some(row => {
      const invAmt = Number(row.invAmt) || 0;
      const invVat = Number(row.invVat) || 0;
      const evidenceType = row.evidenceType;
      
      if (!evidenceType) return false;
      
      // VAT가 0원이어야 하는 증빙유형
      if (ZERO_VAT_TYPES.includes(EVIDENCE_TYPES[evidenceType])) {
        return invVat != 0;
      }
      
      // 세금계산서인 경우 VAT가 원화금액의 10%여야 함
      if (evidenceType == 'TAX_INVOICE') {
        const expectedVat = Math.round(invAmt * 0.1);
        return invVat != expectedVat;
      }
      
      // 기타 증빙유형은 사용자가 입력한 VAT 값 그대로 허용
      return false;
    });

    if (hasVatError) {
      enqueueSnackbar('VAT 계산이 올바르지 않습니다. 증빙유형에 따른 VAT를 확인해 주세요.', { variant: 'error' });
      return;
    }

    // 총합계 일치 확인 - sumVat 기준으로 변경
    const expectedTotal = Number(totalAmount) + Number(sumVat); // totalVat -> sumVat로 변경
    if (sumTot !== expectedTotal) {
      enqueueSnackbar(`총 합계가 일치하지 않습니다. 예상: ₩${getNumberComma(expectedTotal)}, 실제: ₩${getNumberComma(sumTot)}`, { variant: 'error' });
      return;
    }

    // 계획(선금/중도금/잔금) 쪼개기를 한 회차는 계획 합계 = 회차 합계여야 한다 - 화면에서
    // 한 번 더 막아둔다(BillPlanEditDialog도 저장 전 막지만, 서버 createPlansForRound가
    // 최종 방어선이다). 2026-10-21 수금/지급 계획 N개 2차 릴리스(add-mode).
    const planSumError = rows.find(row => {
      if (!row.planLines || row.planLines.length === 0) return false;
      const planSum = row.planLines.reduce((acc, p) => acc + (Number(p.planAmt) || 0), 0);
      return planSum !== (Number(row.invTot) || 0);
    });
    if (planSumError) {
      enqueueSnackbar('계획 합계가 회차 금액과 일치하지 않는 회차가 있습니다. "선금/중도금/잔금"에서 다시 확인해 주세요.', { variant: 'error' });
      return;
    }

    // planLines(화면 상태 이름) -> plans(서버 BillInvDto.plans 계약) 매핑해서 올려보낸다.
    const rowsToSave = rows.map(row => {
      if (!row.planLines || row.planLines.length === 0) return row;
      return { ...row, plans: row.planLines };
    });

    onSave(rowsToSave);
  }, [rows, sumAmt, sumVat, sumTot, totalAmount, onSave, enqueueSnackbar]); // totalVat 제거

  // 닫기 핸들러
  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  // 셀 렌더러 - invTot 필드 readonly 처리 추가
  const DataCell = useCallback(({ row, column }) => {
    const { field, type, conditionalField, conditionalValues, options } = column;
    const value = row[field];
    const isDisabled = row.reqCompYn === 'Y'; // 완료된 행은 편집 불가
    const isInvTotField = field === 'invTot'; // invTot 필드는 항상 readonly
    
    // 조건부 필드 표시 여부 확인
    if (conditionalField && conditionalValues) {
      const conditionalValue = row[conditionalField];
      if (!conditionalValues.includes(conditionalValue)) {
        return <TableCell>-</TableCell>;
      }
    }
  
    // 보기 모드 또는 invTot 필드는 항상 readonly로 표시
    if (isView || isInvTotField) {
      if (type === 'number') {
        return (
          <TableCell>
            <Typography sx={isInvTotField ? { fontWeight: 'bold', color: 'primary.main' } : {}}>
              ₩{getNumberComma(value)}
            </Typography>
          </TableCell>
        );
      }
      if (type === 'date') {
        return (
          <TableCell>
            <Typography>{value ? dayjs(value).format('YYYY-MM-DD') : '-'}</Typography>
          </TableCell>
        );
      }
      if (type === 'select') {
        const option = options.find(opt => opt.value === value);
        return (
          <TableCell>
            <Typography>{option ? option.label : '-'}</Typography>
          </TableCell>
        );
      }
      // string 타입 처리
      return (
        <TableCell>
          <Typography>{value || '-'}</Typography>
        </TableCell>
      );
    }
  
    // 편집 모드 (invTot 제외)
    if (type === 'date') {
      return (
        <TableCell>
          <DateEditInputCell
            value={value}
            onChange={(newValue) => handleCellChange(row.id, field, newValue)}
            disabled={isDisabled}
          />
        </TableCell>
      );
    }
    if (type === 'number') {
      const isVatField = field === 'invVat';
      const isZeroVatType = row.evidenceType && ZERO_VAT_TYPES.includes(EVIDENCE_TYPES[row.evidenceType]);
      const isTaxInvoiceType = row.evidenceType === 'TAX_INVOICE';
      // VAT 필드가 자동 계산되는 경우 (0원 증빙유형 또는 세금계산서)는 편집 불가
      const specialDisabled = isVatField && (isZeroVatType || isTaxInvoiceType);
      
      return (
        <TableCell>
          <NumberEditInputCell
            value={value || 0}
            onChange={(e) => handleCellChange(row.id, field, e.target.value)}
            disabled={isDisabled || specialDisabled}
          />
          {isVatField && isZeroVatType && (
            <Tooltip title="선택한 증빙유형은 VAT가 적용되지 않습니다">
              <IconButton size="small" sx={{ ml: 1 }}>
                <InfoIcon fontSize="small" color="info" />
              </IconButton>
            </Tooltip>
          )}
          {isVatField && isTaxInvoiceType && (
            <Tooltip title="세금계산서는 VAT가 자동 계산됩니다 (원화금액의 10%)">
              <IconButton size="small" sx={{ ml: 1 }}>
                <InfoIcon fontSize="small" color="info" />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
      );
    }
    if (type === 'select') {
      return (
        <TableCell>
          <SelectEditInputCell
            value={value}
            onChange={(e) => handleCellChange(row.id, field, e.target.value)}
            disabled={isDisabled}
            options={options}
            label={column.header}
          />
        </TableCell>
      );
    }
    // string 타입용 텍스트 입력
    return (
      <TableCell>
        <TextEditInputCell
          value={value}
          onChange={(e) => handleCellChange(row.id, field, e.target.value)}
          disabled={isDisabled}
        />
      </TableCell>
    );
  }, [isView, handleCellChange]);

  // 표시할 컬럼 필터링
  const visibleColumns = COLUMNS.filter(col => !col.hidden);
  
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
      <DialogTitle>계산서 분할 상세</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3, px: 1 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="subtitle1" sx={{ width: 80, fontWeight: 'bold' }}>종류:</Typography>
                  <Typography>{type}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="subtitle1" sx={{ width: 80, fontWeight: 'bold' }}>업체명:</Typography>
                  <Typography>{partnerName}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="subtitle1" sx={{ width: 80, fontWeight: 'bold' }}>분할 개수:</Typography>
                  {isView
                    ? <Typography>{localInstallmentCount}</Typography>
                    : <TextField
                        type="number"
                        size="small"
                        value={localInstallmentCount}
                        onChange={handleCountChange}
                        inputProps={{ min: 1 }}
                        sx={{ width: 80 }}
                      />
                  }
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: { xs: 'flex-start', md: 'flex-end' },
                gap: 1
              }}>
                <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold', width: 240, display: 'flex', justifyContent: 'space-between' }}>
                  <span>총 발행 금액:</span>
                  <span>₩{getNumberComma(totalAmount)}</span>
                </Typography>
                {/* <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold', width: 240, display: 'flex', justifyContent: 'space-between' }}>
                  <span>총 VAT:</span>
                  <span>₩{getNumberComma(totalVat)}</span>
                </Typography> */}
                {!isView && (
                  <Chip
                    label={balanced ? '금액 일치' : `차액 ₩${getNumberComma(diffAmt)}`}
                    color={balanced ? 'success' : 'error'}
                    variant={balanced ? 'outlined' : 'filled'}
                    sx={{ fontWeight: 700 }}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ mb: 3 }} />

        
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {visibleColumns.map(col => (
                      <TableCell key={col.field}>{col.header}</TableCell>
                    ))}
                    <TableCell>계획</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map(row => (
                    <TableRow
                      key={row.id}
                      sx={row.reqCompYn === 'Y' ? { backgroundColor: 'rgba(0, 0, 0, 0.04)' } : {}}
                    >
                      {visibleColumns.map(col => (
                        <DataCell
                          key={col.field}
                          row={row}
                          column={col}
                        />
                      ))}
                      <TableCell>
                        <Button size="small" onClick={() => handleOpenPlan(row)}>
                          선금/중도금/잔금
                        </Button>
                        {!row.invUuid && row.planLines && row.planLines.length > 1 && (
                          <Chip size="small" label={`${row.planLines.length}개로 분할`} color="primary"
                            variant="outlined" sx={{ ml: 0.5 }} />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box display="flex" justifyContent="flex-end" gap={4} mt={1}>
              <Typography variant="subtitle1"><strong>합계 금액:</strong> ₩{getNumberComma(sumAmt)}</Typography>
              <Typography variant="subtitle1"><strong>합계 VAT:</strong> ₩{getNumberComma(sumVat)}</Typography>
              <Typography variant="subtitle1" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                <strong>총 합계:</strong> ₩{getNumberComma(sumTot)}
              </Typography>
            </Box>
        
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>취소</Button>
        {!isView && (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading || !balanced}
          >
            확인
          </Button>
        )}
      </DialogActions>

      <BillPlanEditDialog
        open={Boolean(planTarget)}
        row={planTarget}
        submitting={planSubmitting}
        onClose={() => setPlanTarget(null)}
        onSave={handleSavePlan}
      />
    </Dialog>
  );
}